import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { Promotion, OfferTypeFilter, CyobRewardContentFragment } from '@/models';
import { ui } from '@/constants';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { AccountPageLoadEvent } from '@/services/analytics/events';
import { Routes } from '@/router';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { RewardsPageLoadEvent } from '@/services/analytics/events/rewards-page-load-event';

@Component({
  name: 'CmgMemberLoyaltyRewards',
})
export default class CmgMemberLoyaltyRewards extends Vue {
  rewards: Promotion[] = [];
  viewingAll = false;
  cyobRewardCard: CyobRewardContentFragment = {};

  @Watch('showCyobRewardCard', { immediate: true })
  async onShowCyobRewardCardChange(newValue: boolean) {
    if (newValue) {
      await this.$services.config.setCyobRewardCard();
      await this.$services.config.setLegalText();
      this.cyobRewardCard = this.$services.config.getCyobRewardCard() || {};
    }
  }

  get rewardsItems() {
    const { promotions } = this.$appStore.state.customer.wallet;

    return this.viewingAll ? promotions : promotions.slice(0, 3);
  }

  //the full count, regardless of viewingAll
  get rewardsCount() {
    return this.$appStore.state.customer.wallet.promotions.length;
  }

  get isLoggedIn() {
    return this.$services.customer.isLoggedIn() === true;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get rewardsItemsStyle() {
    return this.rewardsItems.length > 0 ? {} : { justifyContent: 'center' };
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get userIsGuacMode(): boolean {
    return this.$appStore.getters.isCustomerGuacMode;
  }

  get refreshIcon(): string {
    return this.$appStore.state.cms.images.icons.refreshGold.default;
  }

  get shouldShowRefresh(): boolean {
    return this.$appStore.state.reward.shouldShowRefresh;
  }

  get medallionFood(): string {
    return this.$appStore.state.cms.images.logos.medallionFood.default;
  }

  get medallionGoods(): string {
    return this.$appStore.state.cms.images.logos.medallionGoods.default;
  }

  get medallionGiving(): string {
    return this.$appStore.state.cms.images.logos.medallionGiving.default;
  }

  get availableMedallionOfferTypes() {
    //Determining applicable medallion offer type images based on the rewards returned from reward store
    const offerTypes = [...new Set<OfferTypeFilter>(this.$appStore.state.reward.rewards.map(item => item.offerType as OfferTypeFilter))];
    let medallions: string[] = [];

    for (const offerType of offerTypes) {
      switch (offerType) {
        case 'Food':
          medallions.push(this.$appStore.state.cms.images.logos.medallionFood.default);
          break;
        case 'Giving':
          medallions.push(this.$appStore.state.cms.images.logos.medallionGiving.default);
          break;
        case 'Goods':
          medallions.push(this.$appStore.state.cms.images.logos.medallionGoods.default);
          break;
      }
    }

    return medallions;
  }

  get isServiceOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isChooseYourOwnBirthdayEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isChooseYourOwnBirthdayEnabled;
  }

  get showCyobRewardCard(): boolean {
    if (!this.isServiceOnline || !this.isRewardsOnline || !this.isChooseYourOwnBirthdayEnabled) {
      return false;
    }

    return this.$appStore.getters[CUSTOMER_GETTERS.IS_CYOB_REWARD_ELIGIBLE];
  }

  get birthdayMedallion(): string {
    return this.$appStore.state.cms.images.loyalty.birthdayMedallion.default;
  }

  get cyobRewardCardTitle(): string {
    return this.cyobRewardCard?.title || 'Happy Birthday!';
  }

  get cyobRewardCardSubtitle(): string {
    return this.cyobRewardCard?.subtitle || 'Enjoy a Birthday Reward, on us';
  }

  get cyobRewardCardCta(): string {
    return 'Choose Your Reward';
  }

  get cyobTermsAndConditionsBodyText(): string {
    return this.$services.config.getLegalText()?.chooseYourOwnBirthday;
  }

  onAddReward(event: any) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: AnalyticsEventAction.AddRewardClick,
      label: AnalyticsEventLabel.AddRewardClick,
    });
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('addReward', true);
    this.$services.analytics.send(new AccountPageLoadEvent('rewards-add-rewards'));
    this.$services.ui.firstFocus();
  }

  onClickViewRewardsExchange(event: any) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardGeneralOnboard,
      action: AnalyticsEventAction.ViewRewardExchangeCtaClick,
      label: AnalyticsEventLabel.ViewRewardExchangeClick,
    });
    this.$router.push({ name: Routes.RewardsExchange });
  }

  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  onViewAll() {
    this.viewingAll = true;
    Vue.nextTick(() => this.$services.ui.focusFirstDiv('.rewards-container'));
  }

  async onClickOpenCyobModal() {
    try{
      this.$services.ui.setAppPepperVisibility(true);
      await this.$services.reward.getCyobOffers();

      const hasCyobRewards = this.$appStore.state.reward.cyobRewards.length > 0;

      if(hasCyobRewards) {
        this.$services.ui.setModalVisibility('cyobRewardModal', true, { showLaterOption: false });

        this.$services.analytics.setIsCyobModalFromAutoPopup(false);

        this.$services.analytics.applyOne({
          web: {
            webInteraction: {
              linkClicks: { value: 1 },
              linkName: this.cyobRewardCardCta.toUpperCase(),
              linkTitle: this.cyobRewardCardTitle.toUpperCase(),
              linkSection: "rewards-home-logged-in-cards",
              name: this.cyobRewardCardCta.toUpperCase(),
              type: "other"
            },
            webPageDetails: {}
          }
        })

        this.$services.analytics.send(new RewardsPageLoadEvent('choose-birthday-reward'));
      } else {
        this.$services.ui.setModalVisibility('tryAgainModal', true, { onClickTryAgain: this.onClickOpenCyobModal });
      }
      
      Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
    } catch {
      // Shows modal in error state because the reward service flags will be false
      this.$services.ui.setModalVisibility('cyobRewardModal', true, { showLaterOption: false });
      Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
    } finally {
      this.$services.ui.setAppPepperVisibility(false);
    }
  }

  onClickTermsAndConditions(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: this.cyobTermsAndConditionsBodyText });
    this.$services.ui.firstFocus();
  }
}
